/**
 * Shared sticker geometry for DOM/SVG and Canvas2D — one pure layout.
 * Intrinsic shape sizes are normalized to a unit box, then scaled and clamped
 * so the rotated AABB stays inside the safe frame margins.
 */
import type {
  CreativeStickerInstance,
  ProceduralStickerShape,
} from "./creative-render-state.js";

export const STICKER_COLOR_HEX: Record<string, string> = {
  coral: "#e85d4c",
  cyan: "#3db8c5",
  amber: "#e8a838",
  gold: "#d4a017",
  white: "#f5f5f0",
};

/** Falls back to coral for an unmapped tag OR a missing/undefined tag — a
 *  single fallback point so call sites never need their own `|| "coral"`. */
export function stickerColor(colorTag: string | undefined): string {
  if (!colorTag) return STICKER_COLOR_HEX.coral!;
  return STICKER_COLOR_HEX[colorTag] ?? STICKER_COLOR_HEX.coral!;
}

export interface StickerLayoutResult {
  /** Center X in pixels. */
  cx: number;
  /** Center Y in pixels. */
  cy: number;
  /** Unrotated width in pixels. */
  width: number;
  /** Unrotated height in pixels. */
  height: number;
  rotationDeg: number;
  color: string;
  text: string;
  shape: ProceduralStickerShape;
  /** Suggested font size for text/badge (px). */
  fontSize: number;
  /** Shell-only curated art override (Fluent Emoji / functional SVG). When
   *  set, Canvas2D/DOM drawers prefer the image over the procedural shape. */
  artSrc?: string;
}

/** Intrinsic unscaled aspect for each procedural shape (w, h) in unit space. */
const SHAPE_INTRINSIC: Record<ProceduralStickerShape, { w: number; h: number }> = {
  badge: { w: 1.35, h: 0.7 },
  burst: { w: 1.1, h: 1.1 },
  arrow: { w: 1.6, h: 0.7 },
  underline: { w: 2.2, h: 0.28 },
  frame: { w: 2.4, h: 1.8 },
  dot: { w: 0.7, h: 0.7 },
  star: { w: 1, h: 1 },
  text: { w: 1.4, h: 0.75 },
};

const SAFE_MARGIN = 0.04;

/**
 * Pure sticker layout shared by DOM and Canvas.
 * Anchors are normalized 0–1; scale is relative; rotation in degrees.
 */
export function layoutSticker(
  sticker: CreativeStickerInstance,
  frameW: number,
  frameH: number,
  options?: { safeMargin?: number },
): StickerLayoutResult {
  const margin = options?.safeMargin ?? SAFE_MARGIN;
  const safeX0 = frameW * margin;
  const safeY0 = frameH * margin;
  const safeX1 = frameW * (1 - margin);
  const safeY1 = frameH * (1 - margin);
  const safeW = Math.max(1, safeX1 - safeX0);
  const safeH = Math.max(1, safeY1 - safeY0);

  // Art (image) stickers render into a SQUARE box that matches the source SVG's
  // 1:1 canvas — otherwise the box would inherit the underlying procedural
  // shape's aspect (e.g. the wide/short "text" glyph), leaving the selection
  // outline hugging only the top of the square art (looked "too high"). The
  // procedural fallback keeps its own shape aspect.
  const ART_INTRINSIC = { w: 1.4, h: 1.4 } as const;
  const intrinsic = sticker.artSrc
    ? ART_INTRINSIC
    : SHAPE_INTRINSIC[sticker.shape] ?? SHAPE_INTRINSIC.badge;
  const unit = Math.min(frameW, frameH) * 0.14;
  let w = unit * intrinsic.w * sticker.scale;
  let h = unit * intrinsic.h * sticker.scale;
  let cx = sticker.x * frameW;
  let cy = sticker.y * frameH;
  const rotationDeg = ((sticker.rotationDeg % 360) + 360) % 360;
  const rad = (rotationDeg * Math.PI) / 180;

  // Rotated AABB half-extents
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  let halfAabbW = (w * cos + h * sin) / 2;
  let halfAabbH = (w * sin + h * cos) / 2;

  // Scale down uniformly if AABB exceeds safe box
  const maxHalfW = safeW / 2;
  const maxHalfH = safeH / 2;
  const fit = Math.min(1, maxHalfW / Math.max(1e-6, halfAabbW), maxHalfH / Math.max(1e-6, halfAabbH));
  if (fit < 1) {
    w *= fit;
    h *= fit;
    halfAabbW *= fit;
    halfAabbH *= fit;
  }

  // Clamp center so full AABB stays in safe area
  cx = clamp(cx, safeX0 + halfAabbW, safeX1 - halfAabbW);
  cy = clamp(cy, safeY0 + halfAabbH, safeY1 - halfAabbH);

  const fontSize = Math.max(8, Math.round(h * (sticker.shape === "text" ? 0.55 : 0.38)));

  return {
    cx,
    cy,
    width: w,
    height: h,
    rotationDeg,
    color: stickerColor(sticker.colorTag),
    text: sticker.text,
    shape: sticker.shape,
    fontSize,
    artSrc: sticker.artSrc,
  };
}

/** @deprecated Prefer layoutSticker — kept as thin adapter for older call sites. */
export function stickerLayout(
  sticker: CreativeStickerInstance,
  width: number,
  height: number,
): {
  x: number;
  y: number;
  w: number;
  h: number;
  rotationDeg: number;
  color: string;
  text: string;
  shape: ProceduralStickerShape;
  artSrc?: string;
} {
  const L = layoutSticker(sticker, width, height);
  return {
    x: L.cx,
    y: L.cy,
    w: L.width,
    h: L.height,
    rotationDeg: L.rotationDeg,
    color: L.color,
    text: L.text,
    shape: L.shape,
    artSrc: L.artSrc,
  };
}

function clamp(n: number, lo: number, hi: number): number {
  if (lo > hi) return (lo + hi) / 2;
  return Math.max(lo, Math.min(hi, n));
}
